import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Topbar } from "@/components/layout/Topbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { RefreshCcw, PencilLine, Inbox } from "lucide-react";
import { api } from "@/lib/api";
import { useSelectedDate } from "@/context/DateContext";
import { StatusBadge } from "@/components/rikkus/StatusBadge";
import { TimestampET } from "@/components/rikkus/TimestampET";

export default function ManualResultsPage() {
    const { date } = useSelectedDate();
    const nav = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = () => {
        setLoading(true);
        setError(null);
        api.get(`/manual-results/queue?date=${date}&track=SAR`)
            .then((r) => setItems(r.data || []))
            .catch((e) => setError(e?.response?.data?.detail || e.message))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, [date]);

    return (
        <>
            <Topbar
                title="Orden de llegada — Entrada manual"
                subtitle="Cola de carreras con TOP_K K≥5 pendientes de completar (5º-último)"
            />
            <div className="p-3 md:p-4 space-y-4 flex-1 overflow-auto">
                <Card>
                    <CardHeader className="pb-3 flex flex-row items-center justify-between">
                        <CardTitle className="text-sm">Bandeja — {date}</CardTitle>
                        <Button
                            data-testid="manual-results-refresh-btn"
                            variant="outline"
                            size="sm"
                            className="h-8 text-xs"
                            onClick={load}
                        >
                            <RefreshCcw className="h-3.5 w-3.5 mr-2" /> Refrescar
                        </Button>
                    </CardHeader>
                    <CardContent>
                        {loading && <Skeleton className="h-32 w-full" />}
                        {!loading && error && (
                            <div className="text-xs text-danger font-mono py-4">{error}</div>
                        )}
                        {!loading && !error && items.length === 0 && (
                            <div
                                data-testid="manual-results-empty-state"
                                className="py-16 flex flex-col items-center justify-center text-center gap-3"
                            >
                                <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                                    <Inbox className="h-6 w-6 text-muted-foreground" />
                                </div>
                                <div className="space-y-1">
                                    <div className="text-sm font-medium">Nada pendiente</div>
                                    <p className="text-xs text-muted-foreground max-w-md">
                                        No hay carreras con ofertas TOP_K K≥5 y posiciones sin completar para {date}.
                                    </p>
                                </div>
                            </div>
                        )}
                        {!loading && !error && items.length > 0 && (
                            <div className="rounded-md border border-border overflow-hidden">
                                <Table data-testid="manual-results-queue-table">
                                    <TableHeader>
                                        <TableRow className="bg-muted/40 hover:bg-muted/40">
                                            <TableHead className="h-8 text-[11px]">R</TableHead>
                                            <TableHead className="h-8 text-[11px]">Post</TableHead>
                                            <TableHead className="h-8 text-[11px] text-right">Campo</TableHead>
                                            <TableHead className="h-8 text-[11px] text-right">TOP_K máx</TableHead>
                                            <TableHead className="h-8 text-[11px] text-right">Ofertas</TableHead>
                                            <TableHead className="h-8 text-[11px] text-right">Pos. vacías</TableHead>
                                            <TableHead className="h-8 text-[11px]">Estado</TableHead>
                                            <TableHead className="h-8 text-[11px]"></TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {items.map((it) => (
                                            <TableRow
                                                key={it.race_id}
                                                data-testid={`manual-results-queue-row-${it.race_id}`}
                                                className="h-9"
                                            >
                                                <TableCell className="font-mono text-xs">R{it.race_number}</TableCell>
                                                <TableCell><TimestampET value={it.scheduled_post_at} /></TableCell>
                                                <TableCell className="text-right font-mono text-xs">
                                                    {it.field_size_final ?? "—"}
                                                </TableCell>
                                                <TableCell className="text-right font-mono text-xs">{it.top_k_max}</TableCell>
                                                <TableCell className="text-right font-mono text-xs">{it.offers_count}</TableCell>
                                                <TableCell className="text-right font-mono text-xs">
                                                    <span className="text-warn">{it.finish_null_count}</span>
                                                </TableCell>
                                                <TableCell>
                                                    {it.has_any_results
                                                        ? <StatusBadge kind="partial">PARCIAL</StatusBadge>
                                                        : <StatusBadge kind="pending">SIN RESULTADOS</StatusBadge>}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <Button
                                                        data-testid={`manual-results-open-btn-${it.race_id}`}
                                                        variant="default"
                                                        size="sm"
                                                        className="h-7 px-2 text-xs"
                                                        onClick={() => nav(`/manual-results/${it.race_id}`)}
                                                    >
                                                        <PencilLine className="h-3 w-3 mr-1" /> Completar
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="py-4 text-[11px] text-muted-foreground space-y-1">
                        <div><strong className="text-foreground">Reglas:</strong></div>
                        <ul className="list-disc pl-4 space-y-0.5">
                            <li>Completar una posición vacía (<code>NULL</code>) es libre: queda como <code>MANUAL_ENTRY</code> con <code>entered_by</code>/<code>entered_at</code>.</li>
                            <li>Sobrescribir una posición ya rellenada por <code>AUTOMATED_CAPTURE</code> requiere <code>revision_note</code> explícito (modal de confirmación).</li>
                            <li>El motor Plackett-Luce necesita el <code>finish_position</code> completo — sin esto, TOP_K con K≥5 no puede validarse.</li>
                        </ul>
                    </CardContent>
                </Card>
            </div>
        </>
    );
}
